import React, { useEffect, useMemo, useState } from 'react';
import { FileText, Download, Loader2, X, Wand2, Pencil, RotateCcw, Plus, Trash2, ListPlus, Eye, ArrowLeft, Save, FolderOpen, Bookmark } from 'lucide-react';
import api, { clientsAPI, propertiesAPI } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';

// Groupe les champs par section pour un rendu propre
const groupBySection = (fields) => {
  const groups = {};
  (fields || []).forEach(f => {
    const s = f.section || 'Autres champs';
    if (!groups[s]) groups[s] = [];
    groups[s].push(f);
  });
  return groups;
};

// Retire le préfixe "property_" et cherche dans l'objet property
const getPrefillValue = (fieldKey, client, property) => {
  if (fieldKey.startsWith('property_')) {
    const propKey = fieldKey.slice('property_'.length);
    return property?.[propKey] ?? '';
  }
  return client?.[fieldKey] ?? '';
};

const FormCard = ({ form, onOpen }) => (
  <div className="rounded-xl border-2 border-gray-100 bg-white p-5 hover:border-amber-300 hover:shadow-md transition-all" data-testid={`form-card-${form.key}`}>
    <div className="flex items-start gap-3">
      <div className="w-12 h-12 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
        <FileText className="w-6 h-6" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-base font-bold text-[#1B3A4B]">{form.title}</h3>
        <p className="text-xs text-gray-500 mt-1 leading-relaxed">{form.description}</p>
        <div className="mt-3">
          <Button size="sm" onClick={() => onOpen(form)}
                  className="bg-amber-600 hover:bg-amber-700 text-white" data-testid={`form-open-${form.key}`}>
            <Pencil className="w-4 h-4 mr-1" /> Éditer & Imprimer
          </Button>
        </div>
      </div>
    </div>
  </div>
);

const emptyArticle = (variant = 'full') => {
  if (variant === 'simple') return { description: '', quantity: 1 };
  if (variant === 'inventory') return { description: '', quantity: 1, state: '', observations: '' };
  return { description: '', quantity: 1, unit: '', unit_price: '', total: '' };
};

const ArticlesEditor = ({ articles, variant, onChange }) => {
  const isFull = variant === 'full';
  const isInv = variant === 'inventory';

  const update = (i, key, val) => {
    const next = articles.map((a, idx) => idx === i ? { ...a, [key]: val } : a);
    // Auto-compute total for full variant
    if (isFull && (key === 'quantity' || key === 'unit_price')) {
      const q = parseFloat(next[i].quantity) || 0;
      const p = parseFloat(next[i].unit_price) || 0;
      if (q && p) next[i].total = (q * p).toString();
    }
    onChange(next);
  };
  const add = () => onChange([...articles, emptyArticle(variant)]);
  const remove = (i) => onChange(articles.filter((_, idx) => idx !== i));

  const subtotal = isFull
    ? articles.reduce((s, a) => s + ((parseFloat(a.quantity) || 0) * (parseFloat(a.unit_price) || 0)), 0)
    : 0;

  return (
    <div className="space-y-2" data-testid="articles-editor">
      <div className="hidden md:grid grid-cols-12 gap-2 text-[10px] font-bold uppercase text-gray-500 px-2">
        <div className="col-span-1">#</div>
        <div className={isFull ? 'col-span-4' : isInv ? 'col-span-4' : 'col-span-9'}>Description</div>
        <div className={isFull ? 'col-span-1' : 'col-span-2'}>Qté</div>
        {isFull && <div className="col-span-1">Unité</div>}
        {isFull && <div className="col-span-2 text-right">PU (FCFA)</div>}
        {isFull && <div className="col-span-2 text-right">Total (FCFA)</div>}
        {isInv && <div className="col-span-2">État</div>}
        {isInv && <div className="col-span-3">Observations</div>}
        <div className="col-span-1"></div>
      </div>
      {articles.map((a, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 items-center bg-gray-50 border rounded-md p-2"
             data-testid={`article-row-${i}`}>
          <div className="col-span-1 text-xs font-mono text-gray-500">#{i + 1}</div>
          <Input
            className={`h-9 text-sm ${isFull ? 'col-span-4' : isInv ? 'col-span-4' : 'col-span-9'}`}
            placeholder="Description"
            value={a.description || ''}
            onChange={e => update(i, 'description', e.target.value)}
            data-testid={`article-desc-${i}`}
          />
          <Input
            type="number" min="0" step="0.5"
            className={`h-9 text-sm ${isFull ? 'col-span-1' : 'col-span-2'}`}
            placeholder="1"
            value={a.quantity ?? ''}
            onChange={e => update(i, 'quantity', e.target.value)}
            data-testid={`article-qty-${i}`}
          />
          {isFull && (
            <Input
              className="h-9 text-sm col-span-1"
              placeholder="u"
              value={a.unit || ''}
              onChange={e => update(i, 'unit', e.target.value)}
              data-testid={`article-unit-${i}`}
            />
          )}
          {isFull && (
            <Input
              type="number" min="0" step="0.01"
              className="h-9 text-sm col-span-2 text-right"
              placeholder="0"
              value={a.unit_price ?? ''}
              onChange={e => update(i, 'unit_price', e.target.value)}
              data-testid={`article-price-${i}`}
            />
          )}
          {isFull && (
            <div className="col-span-2 text-right text-sm font-mono font-semibold text-[#1B3A4B]"
                 data-testid={`article-total-${i}`}>
              {(() => {
                const total = a.total || ((parseFloat(a.quantity) || 0) * (parseFloat(a.unit_price) || 0));
                const n = parseFloat(total) || 0;
                return n ? n.toLocaleString('fr-FR') : '—';
              })()}
            </div>
          )}
          {isInv && (
            <Select value={a.state || ''} onValueChange={v => update(i, 'state', v)}>
              <SelectTrigger className="h-9 text-sm col-span-2" data-testid={`article-state-${i}`}>
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Bon">Bon</SelectItem>
                <SelectItem value="Moyen">Moyen</SelectItem>
                <SelectItem value="Mauvais">Mauvais</SelectItem>
                <SelectItem value="Neuf">Neuf</SelectItem>
              </SelectContent>
            </Select>
          )}
          {isInv && (
            <Input
              className="h-9 text-sm col-span-3"
              placeholder="Notes"
              value={a.observations || ''}
              onChange={e => update(i, 'observations', e.target.value)}
              data-testid={`article-obs-${i}`}
            />
          )}
          <Button type="button" size="sm" variant="ghost"
                  onClick={() => remove(i)}
                  disabled={articles.length <= 1}
                  className="col-span-1 h-9 text-rose-600 hover:text-rose-800 hover:bg-rose-50"
                  data-testid={`article-remove-${i}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      ))}
      <div className="flex justify-between items-center pt-2">
        <Button type="button" size="sm" variant="outline" onClick={add}
                className="border-amber-400 text-amber-700 hover:bg-amber-50"
                data-testid="add-article-btn">
          <Plus className="w-4 h-4 mr-1" /> Ajouter un article
        </Button>
        {isFull && subtotal > 0 && (
          <div className="text-sm font-semibold text-[#1B3A4B]" data-testid="articles-subtotal">
            Sous-total : <span className="font-mono text-amber-700">{subtotal.toLocaleString('fr-FR')} FCFA</span>
          </div>
        )}
      </div>
    </div>
  );
};

const PrintableForms = () => {
  const [catalog, setCatalog] = useState([]);
  const [clients, setClients] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [currentForm, setCurrentForm] = useState(null);
  const [clientId, setClientId] = useState('');
  const [propertyId, setPropertyId] = useState('');
  const [values, setValues] = useState({});
  const [options, setOptions] = useState({});
  const [articles, setArticles] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [showSaveDraft, setShowSaveDraft] = useState(false);
  const [draftTitle, setDraftTitle] = useState('');
  const [savingDraft, setSavingDraft] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [cat, cli, prop] = await Promise.all([
          api.get('/printable-forms/catalog'),
          clientsAPI.getAll().catch(() => ({ data: [] })),
          propertiesAPI.getAll().catch(() => ({ data: [] })),
        ]);
        setCatalog(cat.data.forms || []);
        setClients(cli.data || []);
        setProperties(prop.data || []);
      } catch (e) {
        toast.error('Erreur de chargement du catalogue');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const openEditor = (form) => {
    setCurrentForm(form);
    setClientId('');
    setPropertyId('');
    // Initialise chaque champ à vide
    const initial = {};
    (form.fields || []).forEach(f => { initial[f.key] = ''; });
    setValues(initial);
    // Options par défaut
    const opt = {};
    (form.option_fields || []).forEach(f => { if (f.default) opt[f.key] = f.default; });
    setOptions(opt);
    // Articles : une ligne vide au départ si le formulaire les supporte
    setArticles(form.articles_variant ? [emptyArticle(form.articles_variant)] : []);
    setDraftTitle('');
    setShowSaveDraft(false);
    setModalOpen(true);
    loadDrafts(form.key);
  };

  const loadDrafts = async (formKey) => {
    try {
      const r = await api.get(`/printable-forms/drafts?form_key=${encodeURIComponent(formKey)}`);
      setDrafts(r.data || []);
    } catch (e) {
      setDrafts([]);
    }
  };

  const applyDraft = (d) => {
    if (!currentForm) return;
    setClientId(d.client_id || '');
    setPropertyId(d.property_id || '');
    // Remet tous les champs à vide puis applique
    const next = {};
    (currentForm.fields || []).forEach(f => { next[f.key] = ''; });
    Object.entries(d.fields || {}).forEach(([k, v]) => { next[k] = v; });
    setValues(next);
    setOptions(d.options || {});
    const arts = (d.articles && d.articles.length > 0) ? d.articles : (currentForm.articles_variant ? [emptyArticle(currentForm.articles_variant)] : []);
    setArticles(arts);
    toast.success(`Brouillon "${d.title}" chargé`);
    setShowSaveDraft(false);
  };

  const saveDraft = async () => {
    if (!currentForm) return;
    if (!draftTitle.trim()) return toast.error('Donnez un titre au brouillon');
    setSavingDraft(true);
    try {
      const cleanFields = Object.fromEntries(
        Object.entries(values).filter(([, v]) => v !== '' && v !== null && v !== undefined)
      );
      const cleanArticles = (articles || []).filter(a => (a.description || '').trim());
      await api.post('/printable-forms/drafts', {
        form_key: currentForm.key,
        title: draftTitle.trim(),
        client_id: clientId || null,
        property_id: propertyId || null,
        fields: cleanFields,
        options,
        articles: cleanArticles,
      });
      toast.success('Brouillon sauvegardé');
      setDraftTitle('');
      setShowSaveDraft(false);
      await loadDrafts(currentForm.key);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de sauvegarde');
    } finally {
      setSavingDraft(false);
    }
  };

  const deleteDraft = async (d) => {
    if (!window.confirm(`Supprimer le brouillon "${d.title}" ?`)) return;
    try {
      await api.delete(`/printable-forms/drafts/${d.id}`);
      toast.success('Brouillon supprimé');
      await loadDrafts(currentForm.key);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const applyPrefill = () => {
    if (!currentForm) return;
    const client = clientId ? clients.find(c => c.id === clientId) : null;
    const property = propertyId ? properties.find(p => p.id === propertyId) : null;
    if (!client && !property) {
      toast.info('Sélectionnez un client ou un bien pour le pré-remplissage');
      return;
    }
    const next = { ...values };
    (currentForm.fields || []).forEach(f => {
      const v = getPrefillValue(f.key, client, property);
      if (v !== '' && v !== null && v !== undefined) next[f.key] = String(v);
    });
    setValues(next);
    toast.success('Champs pré-remplis · vous pouvez tout modifier');
  };

  const clearAll = () => {
    if (!currentForm) return;
    const reset = {};
    (currentForm.fields || []).forEach(f => { reset[f.key] = ''; });
    setValues(reset);
    toast.info('Formulaire vidé');
  };

  useEffect(() => {
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
  }, [previewUrl]);

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  const buildPayload = () => {
    const cleanFields = Object.fromEntries(
      Object.entries(values).filter(([, v]) => v !== '' && v !== null && v !== undefined)
    );
    const cleanArticles = (articles || []).filter(a => (a.description || '').trim());
    return {
      client_id: clientId || null,
      property_id: propertyId || null,
      fields: cleanFields,
      options,
      articles: cleanArticles,
      _cleanArticles: cleanArticles,
    };
  };

  const previewPdf = async () => {
    if (!currentForm) return;
    setPreviewing(true);
    try {
      const payload = buildPayload();
      if (currentForm.articles_required && payload._cleanArticles.length === 0) {
        toast.error('Ajoutez au moins un article');
        setPreviewing(false);
        return;
      }
      delete payload._cleanArticles;
      const r = await api.post(`/printable-forms/${currentForm.key}`, payload, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur d\'aperçu');
    } finally {
      setPreviewing(false);
    }
  };

  const generate = async () => {
    if (!currentForm) return;
    setGenerating(true);
    try {
      const payload = buildPayload();
      if (currentForm.articles_required && payload._cleanArticles.length === 0) {
        toast.error('Ajoutez au moins un article');
        setGenerating(false);
        return;
      }
      delete payload._cleanArticles;
      const r = await api.post(`/printable-forms/${currentForm.key}`, payload, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `${currentForm.key}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(link.href), 1000);
      toast.success(`${currentForm.title} téléchargé`);
      closePreview();
      setModalOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de génération');
    } finally {
      setGenerating(false);
    }
  };

  const groupedFields = useMemo(() => groupBySection(currentForm?.fields || []), [currentForm]);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div>;

  return (
    <div className="space-y-6" data-testid="printable-forms-page">
      <div>
        <h1 className="text-3xl font-bold text-[#1B3A4B] flex items-center gap-2">
          <FileText className="w-7 h-7 text-amber-600" /> Formulaires imprimables
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          Chaque formulaire est <b>entièrement éditable</b> avant impression. Pré-remplissez depuis un client/bien puis ajustez chaque champ à votre convenance.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {catalog.map(f => (
          <FormCard key={f.key} form={f} onOpen={openEditor} />
        ))}
      </div>

      <Dialog open={modalOpen} onOpenChange={(o) => { setModalOpen(o); if (!o) closePreview(); }}>
        <DialogContent className="sm:max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="form-editor-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-amber-600" /> {currentForm?.title} — édition manuelle
            </DialogTitle>
            <DialogDescription>{currentForm?.description}</DialogDescription>
          </DialogHeader>

          {currentForm && !previewUrl && (
            <div className="space-y-5 py-2">
              {/* Section Brouillons sauvegardés */}
              <div className="rounded-lg border-2 border-dashed border-indigo-200 bg-indigo-50/40 p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-semibold text-indigo-800 uppercase tracking-wide flex items-center gap-1">
                    <Bookmark className="w-4 h-4" /> Brouillons sauvegardés ({drafts.length})
                  </div>
                  <Button type="button" size="sm" variant="outline"
                          onClick={() => setShowSaveDraft(v => !v)}
                          className="h-7 text-xs border-indigo-400 text-indigo-700 hover:bg-indigo-100"
                          data-testid="toggle-save-draft-btn">
                    <Save className="w-3 h-3 mr-1" /> {showSaveDraft ? 'Annuler' : 'Sauvegarder ce formulaire'}
                  </Button>
                </div>
                {showSaveDraft && (
                  <div className="flex gap-2 mb-2 items-end">
                    <div className="flex-1">
                      <Label className="text-xs">Nom du brouillon</Label>
                      <Input
                        placeholder="Ex: Facture modèle location, Fiche client type…"
                        value={draftTitle}
                        onChange={e => setDraftTitle(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && saveDraft()}
                        data-testid="draft-title-input"
                      />
                    </div>
                    <Button type="button" size="sm" onClick={saveDraft} disabled={savingDraft}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white"
                            data-testid="save-draft-btn">
                      {savingDraft ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    </Button>
                  </div>
                )}
                {drafts.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {drafts.map(d => (
                      <div key={d.id}
                           className="group flex items-center gap-1 bg-white border border-indigo-200 rounded-full pl-3 pr-1 py-1 text-xs hover:border-indigo-400"
                           data-testid={`draft-chip-${d.id}`}>
                        <button type="button"
                                onClick={() => applyDraft(d)}
                                className="flex items-center gap-1 text-indigo-800 font-medium hover:text-indigo-600"
                                data-testid={`draft-load-${d.id}`}
                                title={`Chargé par ${d.created_by_name || ''} · ${(d.updated_at || '').slice(0, 10)}`}>
                          <FolderOpen className="w-3 h-3" /> {d.title}
                          {d.articles?.length > 0 && <span className="text-[10px] opacity-60">({d.articles.length})</span>}
                        </button>
                        <button type="button"
                                onClick={() => deleteDraft(d)}
                                className="text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded-full p-1"
                                data-testid={`draft-delete-${d.id}`}
                                title="Supprimer">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-xs text-gray-500 italic">Aucun brouillon enregistré pour ce formulaire. Cliquez sur &laquo;&nbsp;Sauvegarder ce formulaire&nbsp;&raquo; pour créer un modèle réutilisable.</div>
                )}
              </div>

              {/* Zone de pré-remplissage */}
              <div className="rounded-lg border-2 border-dashed border-amber-300 bg-amber-50/50 p-3">
                <div className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-2 flex items-center gap-1">
                  <Wand2 className="w-4 h-4" /> Pré-remplissage automatique (optionnel)
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
                  {currentForm.prefills?.includes('client_id') && (
                    <div>
                      <Label className="text-xs">Client</Label>
                      <Select value={clientId || 'none'} onValueChange={v => setClientId(v === 'none' ? '' : v)}>
                        <SelectTrigger data-testid="prefill-client-select"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— Aucun —</SelectItem>
                          {clients.slice(0, 300).map(c => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {currentForm.prefills?.includes('property_id') && (
                    <div>
                      <Label className="text-xs">Bien immobilier</Label>
                      <Select value={propertyId || 'none'} onValueChange={v => setPropertyId(v === 'none' ? '' : v)}>
                        <SelectTrigger data-testid="prefill-property-select"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— Aucun —</SelectItem>
                          {properties.slice(0, 300).map(p => (
                            <SelectItem key={p.id} value={p.id}>{p.title || p.property_code}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button type="button" size="sm" variant="outline"
                            onClick={applyPrefill} className="border-amber-400 text-amber-700 hover:bg-amber-100"
                            data-testid="apply-prefill-btn">
                      <Wand2 className="w-4 h-4 mr-1" /> Pré-remplir
                    </Button>
                    <Button type="button" size="sm" variant="ghost" onClick={clearAll}
                            className="text-gray-500" data-testid="clear-fields-btn">
                      <RotateCcw className="w-4 h-4 mr-1" /> Tout vider
                    </Button>
                  </div>
                </div>
              </div>

              {/* Options spécifiques au formulaire (mandate_type, commission…) */}
              {currentForm.option_fields?.length > 0 && (
                <div className="rounded-lg border p-3 bg-gray-50">
                  <div className="text-xs font-semibold text-[#1B3A4B] uppercase tracking-wide mb-2">Options du formulaire</div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {currentForm.option_fields.map(of => (
                      <div key={of.key}>
                        <Label className="text-xs">{of.label}</Label>
                        {of.type === 'select' ? (
                          <Select value={options[of.key] || of.default || ''} onValueChange={v => setOptions({ ...options, [of.key]: v })}>
                            <SelectTrigger data-testid={`opt-${of.key}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {(of.choices || []).map(c => (
                                <SelectItem key={c} value={c}>{c}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input
                            type={of.type || 'text'}
                            step={of.step || undefined}
                            value={options[of.key] || ''}
                            onChange={e => setOptions({ ...options, [of.key]: e.target.value })}
                            data-testid={`opt-${of.key}`}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Champs éditables regroupés par section */}
              {Object.entries(groupedFields).map(([section, fields]) => (
                <div key={section}>
                  <div className="text-xs font-semibold text-[#1B3A4B] uppercase tracking-wide mb-2 border-b border-amber-200 pb-1">
                    {section}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {fields.map(f => (
                      <div key={f.key} className={f.type === 'textarea' ? 'md:col-span-2' : ''}>
                        <Label className="text-xs">
                          {f.label}
                          {f.required && <span className="text-rose-500"> *</span>}
                        </Label>
                        {f.type === 'textarea' ? (
                          <Textarea
                            rows={2}
                            value={values[f.key] || ''}
                            onChange={e => setValues({ ...values, [f.key]: e.target.value })}
                            data-testid={`field-${f.key}`}
                          />
                        ) : f.type === 'select' ? (
                          <Select value={values[f.key] || ''} onValueChange={v => setValues({ ...values, [f.key]: v })}>
                            <SelectTrigger data-testid={`field-${f.key}`}><SelectValue placeholder="—" /></SelectTrigger>
                            <SelectContent>
                              {(f.choices || []).map(c => (
                                <SelectItem key={c} value={c}>{c}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input
                            type={f.type || 'text'}
                            value={values[f.key] || ''}
                            onChange={e => setValues({ ...values, [f.key]: e.target.value })}
                            data-testid={`field-${f.key}`}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* Éditeur d'articles (si supporté par le formulaire) */}
              {currentForm.articles_variant && (
                <div>
                  <div className="text-xs font-semibold text-[#1B3A4B] uppercase tracking-wide mb-2 border-b border-amber-200 pb-1 flex items-center gap-1">
                    <ListPlus className="w-4 h-4 text-amber-600" />
                    {currentForm.articles_label || 'Articles / Lignes'}
                    {currentForm.articles_required && <span className="text-rose-500"> *</span>}
                  </div>
                  <ArticlesEditor
                    articles={articles}
                    variant={currentForm.articles_variant}
                    onChange={setArticles}
                  />
                </div>
              )}
            </div>
          )}

          {/* Panneau d'aperçu PDF */}
          {previewUrl && (
            <div className="py-2 space-y-2" data-testid="pdf-preview-panel">
              <div className="flex items-center justify-between gap-2 border rounded-md bg-amber-50/50 border-amber-200 px-3 py-2">
                <div className="text-sm text-[#1B3A4B] flex items-center gap-2">
                  <Eye className="w-4 h-4 text-amber-700" />
                  <b>Aperçu du document</b> — vérifiez avant de télécharger
                </div>
                <Button size="sm" variant="outline" onClick={closePreview}
                        className="border-amber-400 text-amber-700 hover:bg-amber-100"
                        data-testid="back-to-form-btn">
                  <ArrowLeft className="w-4 h-4 mr-1" /> Retour au formulaire
                </Button>
              </div>
              <iframe
                src={previewUrl}
                title="Aperçu PDF"
                className="w-full rounded-md border border-gray-300"
                style={{ height: '68vh' }}
                data-testid="pdf-preview-iframe"
              />
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)} disabled={generating || previewing}>
              <X className="w-4 h-4 mr-1" /> Annuler
            </Button>
            {!previewUrl && (
              <Button variant="outline" onClick={previewPdf} disabled={generating || previewing}
                      className="border-amber-400 text-amber-700 hover:bg-amber-50"
                      data-testid="form-preview-btn">
                {previewing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Génération de l&apos;aperçu…</> : <><Eye className="w-4 h-4 mr-2" /> Aperçu</>}
              </Button>
            )}
            <Button onClick={generate} disabled={generating || previewing}
                    className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="form-generate-btn">
              {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Génération…</> : <><Download className="w-4 h-4 mr-2" /> Télécharger le PDF</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PrintableForms;
